/**
 * Suite Bottom Tab Navigation
 *
 * Enhanced navigation for ContentSage AI Suite with 5 tabs:
 * - Home (Dashboard)
 * - Apps (Tool Grid)
 * - History (Generated Content)
 * - Services (Agency Marketing)
 *
 * Note: This replaces BottomTabNav for the Suite experience.
 * The original BottomTabNav is kept for backwards compatibility.
 */

import React from 'react';
import { motion } from 'framer-motion';
import {
  Home,
  LayoutGrid,
  Clock,
  Briefcase,
  BarChart3,
  Sparkles,
} from 'lucide-react';

export type SuiteTabId = 'home' | 'apps' | 'analytics' | 'history' | 'services';

interface TabConfig {
  id: SuiteTabId;
  name: string;
  icon: React.ElementType;
  highlight?: boolean;
}

const SUITE_TABS: TabConfig[] = [
  { id: 'home', name: 'Home', icon: Home },
  { id: 'apps', name: 'Apps', icon: LayoutGrid, highlight: true },
  { id: 'analytics', name: 'Analytics', icon: BarChart3 },
  { id: 'history', name: 'History', icon: Clock },
  { id: 'services', name: 'Services', icon: Briefcase },
];

interface SuiteBottomNavProps {
  activeTab: SuiteTabId;
  onTabChange: (tab: SuiteTabId) => void;
}

export const SuiteBottomNav: React.FC<SuiteBottomNavProps> = ({
  activeTab,
  onTabChange,
}) => {
  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-50 pb-[env(safe-area-inset-bottom)]">
      <div className="flex items-center justify-around h-16 px-2">
        {SUITE_TABS.map((tab) => {
          const isActive = activeTab === tab.id;
          const Icon = tab.icon;

          return (
            <button
              key={tab.id}
              onClick={() => onTabChange(tab.id)}
              className="flex flex-col items-center justify-center flex-1 h-full relative touch-target"
              aria-label={tab.name}
              aria-current={isActive ? 'page' : undefined}
            >
              {/* Active indicator */}
              {isActive && (
                <motion.div
                  layoutId="suiteActiveTab"
                  className={`absolute top-0 left-1/2 -translate-x-1/2 w-12 h-1 rounded-b-full ${
                    tab.highlight ? 'bg-gradient-to-r from-indigo-500 to-purple-500' : 'bg-indigo-600'
                  }`}
                  transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                />
              )}

              <motion.div
                animate={{
                  scale: isActive ? 1.1 : 1,
                }}
                transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                className={`flex flex-col items-center gap-1 ${
                  isActive ? 'text-indigo-600' : 'text-gray-500'
                }`}
              >
                {/* Special styling for Apps tab */}
                {tab.highlight && isActive ? (
                  <div className="relative">
                    <Icon
                      className="w-6 h-6 text-indigo-600"
                      strokeWidth={2.5}
                    />
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{
                        duration: 4,
                        repeat: Infinity,
                        ease: 'linear',
                      }}
                      className="absolute -top-1 -right-1"
                    >
                      <Sparkles className="w-3 h-3 text-purple-500" />
                    </motion.div>
                  </div>
                ) : (
                  <Icon
                    className={`w-6 h-6 transition-colors ${
                      isActive ? 'text-indigo-600' : 'text-gray-400'
                    }`}
                    strokeWidth={isActive ? 2.5 : 2}
                  />
                )}
                <span
                  className={`text-xs font-medium transition-colors ${
                    isActive ? 'text-indigo-600' : 'text-gray-500'
                  }`}
                >
                  {tab.name}
                </span>
              </motion.div>
            </button>
          );
        })}
      </div>
    </nav>
  );
};

export default SuiteBottomNav;
